/*
 * Copyright (c) 2020 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { ActionModel, BackdropModel, ComponentModel, ContainerModel, DropdownModel } from '@typescript-kit/view';
import { Change, ChangeOptions, ObservableModel, ObserveModes } from '@typescript-kit/core';

export interface ShellComponentData {
  viewDropdown?: DropdownModel;
  userDropdown?: DropdownModel;
  toggleSidebarAction?: ActionModel;
  isMenuVisible?: boolean;
  menuModel?: ComponentModel;
  // menuHeight?: number;
  isSidebarVisible?: boolean;
  sidebarModel?: ComponentModel;
  isFooterVisible?: boolean;
  alertContainer?: ContainerModel;
  vendorName?: string;
  version?: string;
  backdropModel?: BackdropModel;
}

export class ShellComponentFieldKey {
  static readonly VIEW_DROPDOWN = 'viewDropdown';
  static readonly USER_DROPDOWN = 'userDropdown';
  static readonly TOGGLE_SIDEBAR_ACTION = 'toggleSidebarAction';
  static readonly IS_MENU_VISIBLE = 'isMenuVisible';
  static readonly MENU_MODEL = 'menuModel';
  static readonly IS_SIDEBAR_VISIBLE = 'isSidebarVisible';
  static readonly SIDEBAR_MODEL = 'sidebarModel';
  static readonly IS_FOOTER_VISIBLE = 'isFooterVisible';
  static readonly ALERT_CONTAINER = 'alertContainer';
  static readonly VENDOR_NAME = 'vendorName';
  static readonly VERSION = 'version';
  static readonly BACKDROP_MODEL = 'backdropModel';
}


export class ShellComponentModel extends ObservableModel implements ShellComponentData {
  static readonly KEYS = Object.keys(ShellComponentFieldKey).map((key) => ShellComponentFieldKey[key]);

  constructor(data?: ShellComponentData) {
    super(data, ShellComponentModel.KEYS, { observeMode: ObserveModes.FLAT });
  }

  get viewDropdown(): DropdownModel {
    return this.get(ShellComponentFieldKey.VIEW_DROPDOWN);
  }

  set viewDropdown(value: DropdownModel) {
    this.set(ShellComponentFieldKey.VIEW_DROPDOWN, value);
  }

  get userDropdown(): DropdownModel {
    return this.get(ShellComponentFieldKey.USER_DROPDOWN);
  }

  set userDropdown(value: DropdownModel) {
    this.set(ShellComponentFieldKey.USER_DROPDOWN, value);
  }

  get toggleSidebarAction(): ActionModel {
    return this.get(ShellComponentFieldKey.TOGGLE_SIDEBAR_ACTION);
  }

  set toggleSidebarAction(value: ActionModel) {
    this.set(ShellComponentFieldKey.TOGGLE_SIDEBAR_ACTION, value);
  }

  get isMenuVisible(): boolean {
    return this.get(ShellComponentFieldKey.IS_MENU_VISIBLE);
  }

  set isMenuVisible(value: boolean) {
    this.set(ShellComponentFieldKey.IS_MENU_VISIBLE, value);
  }

  get menuModel(): ComponentModel {
    return this.get(ShellComponentFieldKey.MENU_MODEL);
  }

  set menuModel(value: ComponentModel) {
    this.set(ShellComponentFieldKey.MENU_MODEL, value);
  }

  get isSidebarVisible(): boolean {
    return this.get(ShellComponentFieldKey.IS_SIDEBAR_VISIBLE);
  }

  set isSidebarVisible(value: boolean) {
    this.set(ShellComponentFieldKey.IS_SIDEBAR_VISIBLE, value);
  }

  get sidebarModel(): ComponentModel {
    return this.get(ShellComponentFieldKey.SIDEBAR_MODEL);
  }

  set sidebarModel(value: ComponentModel) {
    this.set(ShellComponentFieldKey.SIDEBAR_MODEL, value);
  }

  get isFooterVisible(): boolean {
    return this.get(ShellComponentFieldKey.IS_FOOTER_VISIBLE);
  }

  set isFooterVisible(value: boolean) {
    this.set(ShellComponentFieldKey.IS_FOOTER_VISIBLE, value);
  }

  get alertContainer(): ContainerModel {
    return this.get(ShellComponentFieldKey.ALERT_CONTAINER);
  }

  set alertContainer(value: ContainerModel) {
    this.set(ShellComponentFieldKey.ALERT_CONTAINER, value);
  }

  get vendorName(): string {
    return this.get(ShellComponentFieldKey.VENDOR_NAME);
  }

  set vendorName(value: string) {
    this.set(ShellComponentFieldKey.VENDOR_NAME, value);
  }

  get version(): string {
    return this.get(ShellComponentFieldKey.VERSION);
  }

  set version(value: string) {
    this.set(ShellComponentFieldKey.VERSION, value);
  }

  get backdropModel(): BackdropModel {
    return this.get(ShellComponentFieldKey.BACKDROP_MODEL);
  }

  set backdropModel(value: BackdropModel) {
    this.set(ShellComponentFieldKey.BACKDROP_MODEL, value);
  }

  setValues(values: ShellComponentData, options?: ChangeOptions): { [p: string]: Change<any> } {
    return super.setValues(values, options);
  }

  protected setupValues(values: ShellComponentData, exclusive: boolean): ShellComponentData {
    if ((exclusive || ShellComponentFieldKey.BACKDROP_MODEL in values) && !(values.backdropModel instanceof BackdropModel)) {
      if (this.backdropModel) {
        this.backdropModel.setValues(values.backdropModel, { exclusive: true });
        values.backdropModel = this.backdropModel;
      } else {
        values.backdropModel = new BackdropModel(values.backdropModel);
      }
    }
    return super.setupValues(values, exclusive) as ShellComponentData;
  }
}
